import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Check, AlertCircle, X, Minus } from "lucide-react";
import { WeightedCompatibilityResult, getImportanceLabel, getImportanceColor } from "@/utils/weightedCompatibility";
import { getScoreColor, getScoreBadge } from "@/utils/compatibilityColors";
import { cn } from "@/lib/utils";

interface StandardizedCompatibilityDisplayProps {
  result: WeightedCompatibilityResult;
  showDetails?: boolean;
  profile1Name?: string;
  profile2Name?: string;
}

export function StandardizedCompatibilityDisplay({
  result,
  showDetails = true,
  profile1Name,
  profile2Name
}: StandardizedCompatibilityDisplayProps) {
  const getMatchColor = (match: boolean | "close" | "partial" | "open" | "not_considered") => {
    if (match === true) return "bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-400";
    if (match === "not_considered") return "bg-muted/30 border-border text-muted-foreground";
    if (match === "close" || match === "partial" || match === "open") {
      return "bg-yellow-500/10 border-yellow-500/30 text-yellow-700 dark:text-yellow-400";
    }
    return "bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-400";
  };

  const getIcon = (match: boolean | "close" | "partial" | "open" | "not_considered") => {
    if (match === true) return <Check className="h-4 w-4" />;
    if (match === "not_considered") return <Minus className="h-4 w-4" />;
    if (match === "close" || match === "partial" || match === "open") {
      return <AlertCircle className="h-4 w-4" />;
    }
    return <X className="h-4 w-4" />;
  };

  const badge = getScoreBadge(result.score);
  const colorClass = getScoreColor(result.score);

  const detailLabels: Record<string, string> = {
    age: "Age",
    height: "Height",
    religiosity: "Religiosity",
    sect: "Sect",
    location: "Location",
    ethnicity: "Ethnicity",
    education: "Education",
    personality: "Personality",
    financial: "Financial Status",
    hijab: "Hijab",
    beard: "Beard",
    masjidAttendance: "Masjid Attendance",
    masjid_attendance: "Masjid Attendance", // Add snake_case version
    maritalStatus: "Marital Status",
    marital_status: "Marital Status", // Add snake_case version
    children: "Children",
  };

  // Prioritize showing criteria that both marked as important
  const sortedDetails = Object.entries(result.details).sort(([keyA, detailA], [keyB, detailB]) => {
    if (!detailA || !detailB) return 0;

    const bothImportantA =
      detailA.importance &&
      (detailA.importance.profile1 === "very_important" || detailA.importance.profile1 === "deal_breaker") &&
      (detailA.importance.profile2 === "very_important" || detailA.importance.profile2 === "deal_breaker");

    const bothImportantB =
      detailB.importance &&
      (detailB.importance.profile1 === "very_important" || detailB.importance.profile1 === "deal_breaker") &&
      (detailB.importance.profile2 === "very_important" || detailB.importance.profile2 === "deal_breaker");

    if (bothImportantA && !bothImportantB) return -1;
    if (!bothImportantA && bothImportantB) return 1;

    return (detailB.points || 0) - (detailA.points || 0);
  });

  const formatStandardizedExplanation = (key: string, detail: any) => {
    // The notes now contain all formatting including importance, just return as-is
    return detail.note || detail.issue || "";
  };

  return (
    <Card className="p-4">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className={cn("text-3xl font-bold", colorClass)}>{result.score}%</div>
            <Badge variant={badge.variant} className="mt-1">
              {badge.text}
            </Badge>
            <p className="text-xs text-muted-foreground mt-1">
              Earned {result.earnedPoints.toFixed(0)} of {result.totalPossiblePoints.toFixed(0)} weighted points
            </p>
          </div>
          <Progress value={result.score} className="w-32" />
        </div>

        {result.weightedFactors.mutualPriorities.length > 0 && (
          <div className="rounded-lg bg-green-500/10 p-3 border border-green-500/20">
            <p className="text-sm font-semibold text-green-700 dark:text-green-400 mb-1">
              ✨ Aligned Priorities!
            </p>
            <p className="text-sm text-muted-foreground">
              Both prioritize: {result.weightedFactors.mutualPriorities.map(key => detailLabels[key] || key).join(", ")}
            </p>
          </div>
        )}

        {result.dealBreakers.length > 0 && (
          <div className="rounded-lg bg-destructive/10 p-3 border border-destructive/20">
            <p className="text-sm font-semibold text-destructive mb-1">Deal Breakers:</p>
            <ul className="text-sm space-y-1">
              {result.dealBreakers.map((breaker, i) => (
                <li key={i} className="flex items-start gap-2">
                  <X className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  <span>{breaker}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {showDetails && (
          <div className="space-y-3 pt-2 border-t">
            <p className="text-sm font-semibold">Compatibility Breakdown:</p>
            <div className="space-y-2">
              {sortedDetails.map(([key, detail]) => {
                if (!detail) return null;

                return (
                  <div
                    key={key}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border transition-colors",
                      getMatchColor(detail.match)
                    )}
                  >
                    <div className="mt-0.5">
                      {getIcon(detail.match)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="font-semibold text-sm">{detailLabels[key]}</span>
                        {detail.points !== undefined && detail.maxPoints !== undefined && detail.maxPoints > 0 && (
                          <Badge variant="outline" className="text-xs shrink-0">
                            {detail.points.toFixed(0)}/{detail.maxPoints.toFixed(0)} pts
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs leading-relaxed whitespace-pre-line">
                        {formatStandardizedExplanation(key, detail)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
